import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Send, FileText } from 'lucide-react';

interface SendVersionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: () => void;
  onSaveDraft: () => void;
  versionType: 'estimate' | 'change_order';
  versionNumber: number;
  isLoading?: boolean;
}

const SendVersionDialog: React.FC<SendVersionDialogProps> = ({
  isOpen,
  onClose,
  onSend,
  onSaveDraft,
  versionType,
  versionNumber,
  isLoading = false
}) => {
  const getTitle = () => {
    if (versionType === 'change_order') {
      return `Send Change Order v${versionNumber}`;
    }
    return `Send Estimate v${versionNumber}`;
  };

  const getDescription = () => {
    if (versionType === 'change_order') {
      return `Would you like to send this change order to the customer now, or save it as a draft?`;
    }
    return `Would you like to send this new estimate version to the customer now, or save it as a draft?`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Send className="h-6 w-6 text-blue-500" />
            <div>
              <DialogTitle>{getTitle()}</DialogTitle>
              <DialogDescription className="mt-1">
                {getDescription()}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-lg border p-4 space-y-2">
            <h4 className="font-medium">What happens next?</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>Send Now:</strong> Customer will be notified and can view the {versionType === 'change_order' ? 'change order' : 'estimate'}</li>
              <li>• <strong>Save as Draft:</strong> {versionType === 'change_order' ? 'Change order' : 'Version'} is saved but not sent to customer</li>
              <li>• You can always send draft versions later</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onSaveDraft}
            disabled={isLoading}
          >
            <FileText className="w-4 h-4 mr-2" />
            Save as Draft
          </Button>
          <Button
            onClick={onSend}
            disabled={isLoading}
          >
            <Send className="w-4 h-4 mr-2" />
            {isLoading ? 'Sending...' : 'Send Now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendVersionDialog;